import React from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Bold,
  ChevronDown,
  FileText,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  Paperclip,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { normalizeMailBody } from '../../../utils/mailBody';
import { handleContentEditableListKeyDown } from '../../../utils/contentEditableLists';
import { markdownToEmailHtml } from '../../../utils/markdownToEmailHtml';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { useDirtySnapshot } from '../../../hooks/useDiscardGuard';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { AppModal } from '../../../components/AppModal';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { mergeNylasAttachmentFiles, validateNylasAttachments, estimateNylasOutgoingBodyBytes } from '../inboxShared';
import { InboxAttachmentPreviewModal } from './InboxAttachmentPreviewModal';
import { fetchInboxAttachmentBlob, type InboxAttachmentPreviewTarget } from '../inboxAttachmentUtils';
import type { DunningStage } from '../../invoices/utils/dunningDocument';
import { dunningPdfFilename } from '../../invoices/utils/dunningDocument';
import { composeFromOptions, mailboxFromChoices, resolvedDefaultFrom } from '../mailboxFromChoices';

type ComposeInboxAccount = {
  id: string;
  email: string;
  email_aliases: string[];
  default_from_email: string | null;
  signature_html: string | null;
  is_private: boolean;
};

export function ReplyToolbarButton({
  icon: Icon,
  title,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className="p-1.5 rounded text-app-text-muted hover:bg-app-hover hover:text-app-text"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export type ComposeEmailModalProps = {
  open: boolean;
  accountId: string | number | null;
  aiDraftEmailId?: string | number | null;
  forwardSourceEmailId?: string | number | null;
  /** Anhänge der weitergeleiteten Mail; werden beim Öffnen geladen und vorausgefüllt. */
  forwardAttachments?: InboxAttachmentPreviewTarget[];
  initialTo?: string;
  initialCc?: string;
  initialSubject?: string;
  initialBody?: string;
  /** Versandart-Adresse. Leer lässt die bisherige Von-Vorauswahl des Postfachs. */
  initialFromEmail?: string | null;
  initialAttachments?: File[];
  signatureHtml?: string | null;
  /** Zusätzig zu `open`, um Formular bei gleichem `open` neu zu füllen (z. B. Inkrement). */
  resetKey?: number | string;
  onClose: () => void;
  onSent?: () => void | Promise<void>;
  /** Wird aufgerufen, wenn der Nutzer im Modal ein anderes Postfach wählt. */
  onAccountChange?: (accountId: string) => void;
  /** Nur z-index-Klassen, z. B. `z-[270]` (wird mit `fixed inset-0` kombiniert). */
  overlayClassName?: string;
  /** Wenn gesetzt, wird die gesendete Mail diesem Projekt zugeordnet. */
  leadId?: string | number | null;
  /** Mahnungsflow: Button zum Erzeugen/Ansehen des Mahnungsdokuments. */
  dunningDocument?: { invoiceId: string; stage: DunningStage; invoiceNumber?: string | null } | null;
  onDunningDocumentCreated?: (leadDocumentId: string) => void;
};

export function ComposeEmailModal({
  open,
  accountId,
  aiDraftEmailId,
  forwardSourceEmailId = null,
  forwardAttachments,
  initialTo = '',
  initialCc = '',
  initialSubject = '',
  initialBody = '',
  initialFromEmail = null,
  initialAttachments,
  signatureHtml = null,
  resetKey = 0,
  onClose,
  onSent,
  onAccountChange,
  overlayClassName = 'z-40',
  leadId = null,
  dunningDocument = null,
  onDunningDocumentCreated,
}: ComposeEmailModalProps) {
  // Das Modal versendet auch aus Projekten, Rechnungen und Kontakten. Versand, Postfachliste und
  // Versandpostfach bleiben ohne Posteingang erreichbar; der KI-Entwurf (/inbox/*-draft) nicht.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const aiDraftAvailable = modulesLoaded && isEnabled('posteingang');
  const { can } = useCapabilities();
  const canSuggestContacts = can('web.kontakte.view');
  const [composeAccountId, setComposeAccountId] = React.useState(
    accountId != null && String(accountId).trim() ? String(accountId) : '',
  );
  const [composeFromEmail, setComposeFromEmail] = React.useState('');
  const [composeSignatureHtml, setComposeSignatureHtml] = React.useState<string | null>(signatureHtml);
  const [sendableAccounts, setSendableAccounts] = React.useState<ComposeInboxAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = React.useState(false);
  const [composeTo, setComposeTo] = React.useState(initialTo);
  const [composeCc, setComposeCc] = React.useState(initialCc);
  const [composeSubject, setComposeSubject] = React.useState(initialSubject);
  const [composeToSuggestions, setComposeToSuggestions] = React.useState<
    Array<{ id: number; label: string; email: string }>
  >([]);
  const [showComposeToDropdown, setShowComposeToDropdown] = React.useState(false);
  const [composeToInteracted, setComposeToInteracted] = React.useState(false);
  const composeToDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const composeToWrapperRef = React.useRef<HTMLDivElement>(null);
  const [composeDraft, setComposeDraft] = React.useState(initialBody);
  const composeEditorRef = React.useRef<HTMLDivElement | null>(null);
  const composeFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [composeAttachments, setComposeAttachments] = React.useState<File[]>([]);
  const [forwardAttachmentsLoading, setForwardAttachmentsLoading] = React.useState(false);
  const forwardLoadSessionRef = React.useRef(0);
  const [showCcField, setShowCcField] = React.useState(false);
  const [composeSending, setComposeSending] = React.useState(false);
  const [aiDraftLoading, setAiDraftLoading] = React.useState(false);
  const [aiDraftOpen, setAiDraftOpen] = React.useState(false);
  const [aiDraftPrompt, setAiDraftPrompt] = React.useState('');
  const [aiDraftError, setAiDraftError] = React.useState<string | null>(null);
  const aiDraftInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const AI_DRAFT_MAX_LINES = 10;

  const syncAiDraftTextareaHeight = React.useCallback(() => {
    const el = aiDraftInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20;
    const maxH = lh * AI_DRAFT_MAX_LINES + (Number.isFinite(padY) ? padY : 0);
    const next = Math.min(el.scrollHeight, maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    if (!aiDraftOpen) return;
    syncAiDraftTextareaHeight();
  }, [aiDraftOpen, aiDraftPrompt, syncAiDraftTextareaHeight]);

  const [sendError, setSendError] = React.useState<string | null>(null);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = React.useState<InboxAttachmentPreviewTarget | null>(null);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = React.useState(false);
  const attachmentTriggerRef = React.useRef<HTMLButtonElement>(null);
  const attachmentMenuRef = React.useRef<HTMLDivElement>(null);
  const [attachmentMenuPos, setAttachmentMenuPos] = React.useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const [dunningPdfBusy, setDunningPdfBusy] = React.useState(false);
  const [dunningPdfError, setDunningPdfError] = React.useState<string | null>(null);
  const attachmentUrlsRef = React.useRef<Map<File, string>>(new Map());

  const dunningPdfName = dunningDocument
    ? dunningPdfFilename(dunningDocument.stage, dunningDocument.invoiceNumber ?? null)
    : null;
  const dunningFile =
    dunningPdfName ? composeAttachments.find((file) => file.name === dunningPdfName) ?? null : null;
  const hasDunningPdf = Boolean(dunningFile);
  const showDunningPreview = Boolean(dunningFile);

  const attachmentUrlFor = React.useCallback((file: File): string => {
    const existing = attachmentUrlsRef.current.get(file);
    if (existing) return existing;
    const url = URL.createObjectURL(file);
    attachmentUrlsRef.current.set(file, url);
    return url;
  }, []);

  React.useEffect(() => {
    const keep = new Set(composeAttachments);
    for (const [file, url] of attachmentUrlsRef.current) {
      if (keep.has(file)) continue;
      URL.revokeObjectURL(url);
      attachmentUrlsRef.current.delete(file);
    }
  }, [composeAttachments]);

  React.useEffect(() => {
    return () => {
      for (const url of attachmentUrlsRef.current.values()) URL.revokeObjectURL(url);
      attachmentUrlsRef.current.clear();
    };
  }, []);

  const openAttachmentPreview = React.useCallback(
    (file: File) => {
      setAttachmentMenuOpen(false);
      setPreviewAttachment({
        downloadUrl: attachmentUrlFor(file),
        filename: file.name,
        contentType: file.type || null,
      });
    },
    [attachmentUrlFor],
  );

  const openAttachmentMenu = React.useCallback(() => {
    const el = attachmentTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(240, Math.min(320, rect.width + 180));
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const top = Math.min(rect.bottom + 4, window.innerHeight - 8);
    setAttachmentMenuPos({ top, left, width });
    setAttachmentMenuOpen(true);
  }, []);

  React.useEffect(() => {
    if (!attachmentMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (attachmentTriggerRef.current?.contains(target)) return;
      if (attachmentMenuRef.current?.contains(target)) return;
      setAttachmentMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAttachmentMenuOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [attachmentMenuOpen]);

  const handleCreateDunningDocument = React.useCallback(async () => {
    if (!dunningDocument || !dunningPdfName) return;
    const existing = composeAttachments.find((file) => file.name === dunningPdfName);
    if (existing) {
      openAttachmentPreview(existing);
      return;
    }
    setDunningPdfBusy(true);
    setDunningPdfError(null);
    try {
      const res = await fetch(`/api/invoices/${encodeURIComponent(dunningDocument.invoiceId)}/dunning-document`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: dunningDocument.stage }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Mahnungsdokument konnte nicht erstellt werden.');
      }
      const blob = await res.blob();
      const file = new File([blob], dunningPdfName, { type: blob.type || 'application/pdf' });
      attachmentUrlFor(file);
      setComposeAttachments((prev) => [...prev.filter((item) => item.name !== dunningPdfName), file]);
      const leadDocumentId = res.headers.get('X-Dunning-Lead-Document-Id')?.trim();
      if (leadDocumentId) onDunningDocumentCreated?.(leadDocumentId);
    } catch (err) {
      setDunningPdfError(err instanceof Error ? err.message : 'Mahnungsdokument konnte nicht erstellt werden.');
    } finally {
      setDunningPdfBusy(false);
    }
  }, [attachmentUrlFor, composeAttachments, dunningDocument, dunningPdfName, onDunningDocumentCreated, openAttachmentPreview]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAccountsLoading(true);
    fetch('/api/inbox/accounts', { credentials: 'include' })
      .then(async (r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        const rows = (Array.isArray(data) ? data : [])
          .filter((a) => a?.nylas_grant_id || a?.is_demo_simulated_connection)
          .map((a) => ({
            id: String(a.id),
            email: String(a.email ?? a.nylas_email ?? '').trim(),
            email_aliases: Array.isArray(a.email_aliases)
              ? a.email_aliases.map((alias: unknown) => String(alias).trim().toLowerCase()).filter(Boolean)
              : [],
            default_from_email: a.default_from_email != null ? String(a.default_from_email) : null,
            signature_html: a.signature_html != null ? String(a.signature_html) : null,
            is_private: Boolean(a.is_private),
          }))
          .filter((a) => a.email);
        setSendableAccounts(rows);
      })
      .catch(() => {
        if (!cancelled) setSendableAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, resetKey]);

  const fromOptions = React.useMemo(() => composeFromOptions(sendableAccounts), [sendableAccounts]);

  const handleComposeFromChange = React.useCallback(
    (nextKey: string) => {
      const option = fromOptions.find((item) => item.key === nextKey);
      if (!option) return;
      setComposeAccountId(option.accountId);
      setComposeFromEmail(option.email);
      const acc = sendableAccounts.find((item) => item.id === option.accountId);
      setComposeSignatureHtml(acc?.signature_html ?? null);
      onAccountChange?.(option.accountId);
    },
    [fromOptions, onAccountChange, sendableAccounts],
  );

  React.useEffect(() => {
    const raw = composeTo.split(',').pop()?.trim() ?? '';
    if (!canSuggestContacts || !raw || raw.length < 2) {
      setComposeToSuggestions([]);
      setShowComposeToDropdown(false);
      return;
    }
    if (composeToDebounceRef.current) clearTimeout(composeToDebounceRef.current);
    composeToDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/contacts');
        if (!res.ok) return;
        const all = (await res.json()) as Array<{
          id: number;
          first_name?: string;
          last_name?: string;
          name?: string;
          email?: string;
          company?: string;
        }>;
        const q = raw.toLowerCase();
        const matches = all
          .filter(
            (c) =>
              c.email &&
              ((c.email ?? '').toLowerCase().includes(q) ||
                `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase().includes(q) ||
                (c.name ?? '').toLowerCase().includes(q) ||
                (c.company ?? '').toLowerCase().includes(q)),
          )
          .slice(0, 6)
          .map((c) => ({
            id: c.id,
            email: c.email ?? '',
            label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || c.email || '',
          }));
        setComposeToSuggestions(matches);
        setShowComposeToDropdown(composeToInteracted && matches.length > 0);
      } catch {
        /* ignore */
      }
    }, 250);
  }, [composeTo, composeToInteracted, canSuggestContacts]);

  const loadForwardAttachments = React.useCallback(
    async (targets: InboxAttachmentPreviewTarget[], session: number) => {
      setForwardAttachmentsLoading(true);
      const results = await Promise.allSettled(
        targets.map(async (target) => {
          const blob = await fetchInboxAttachmentBlob(target.downloadUrl, target.filename);
          return new File([blob], target.filename, {
            type: target.contentType || blob.type || 'application/octet-stream',
          });
        }),
      );
      if (session !== forwardLoadSessionRef.current) return;
      const failed: string[] = [];
      const loaded: File[] = [];
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') loaded.push(result.value);
        else failed.push(targets[idx]?.filename || 'Anhang');
      });
      setComposeAttachments((prev) => {
        let files = prev;
        const skipped: string[] = [];
        let limitError: string | null = null;
        for (const file of loaded) {
          const merged = mergeNylasAttachmentFiles(files, [file]);
          if (merged.error) {
            skipped.push(file.name);
            limitError = merged.error;
          } else {
            files = merged.files;
          }
        }
        const messages: string[] = [];
        if (failed.length) messages.push(`Anhang konnte nicht übernommen werden: ${failed.join(', ')}`);
        if (skipped.length) messages.push(`Nicht übernommen (${limitError}): ${skipped.join(', ')}`);
        setAttachmentError(messages.length ? messages.join(' · ') : null);
        return files;
      });
      setForwardAttachmentsLoading(false);
    },
    [],
  );

  const [composeSessionKey, setComposeSessionKey] = React.useState(0);
  const composeSessionRef = React.useRef<{ open: boolean; resetKey: number | string }>({
    open: false,
    resetKey: 0,
  });

  React.useLayoutEffect(() => {
    if (!open) {
      composeSessionRef.current.open = false;
      forwardLoadSessionRef.current += 1;
      setForwardAttachmentsLoading(false);
      return;
    }
    const isNewSession =
      !composeSessionRef.current.open || composeSessionRef.current.resetKey !== resetKey;
    composeSessionRef.current = { open: true, resetKey };
    if (!isNewSession) return;
    setComposeSessionKey((k) => k + 1);

    setComposeAccountId(accountId != null && String(accountId).trim() ? String(accountId) : '');
    setComposeFromEmail(String(initialFromEmail ?? '').trim().toLowerCase());
    setComposeSignatureHtml(signatureHtml ?? null);
    setComposeTo(initialTo);
    setComposeCc(initialCc);
    setShowCcField(Boolean(initialCc.trim()));
    setComposeSubject(initialSubject);
    setComposeAttachments(initialAttachments?.length ? [...initialAttachments] : []);
    setComposeDraft(initialBody);
    setSendError(null);
    setAttachmentError(null);
    setAiDraftPrompt('');
    setAiDraftError(null);
    setAiDraftOpen(false);
    setComposeToInteracted(false);
    setShowComposeToDropdown(false);
    setPreviewAttachment(null);
    setAttachmentMenuOpen(false);
    setDunningPdfError(null);
    const el = composeEditorRef.current;
    if (el) el.innerText = initialBody;
    const forwardSession = ++forwardLoadSessionRef.current;
    if (forwardAttachments?.length) void loadForwardAttachments(forwardAttachments, forwardSession);
    else setForwardAttachmentsLoading(false);
    // initialAttachments/forwardAttachments absichtlich nicht in deps: neue File[]-Identität pro Parent-Render sonst
    // unnötige Resets; resetKey/open decken neue Anhänge beim Öffnen ab.
  }, [open, resetKey, accountId, initialTo, initialCc, initialSubject, initialBody, initialFromEmail, signatureHtml, loadForwardAttachments]);
  // Basis pro Sitzung und nach dem Nachladen weitergeleiteter Anhänge — Vorbelegung zählt nicht.
  const composeDirty = useDirtySnapshot(
    JSON.stringify([
      composeTo.trim(),
      composeCc.trim(),
      composeSubject.trim(),
      normalizeMailBody(composeDraft).trim(),
      composeAttachments.map((file) => file.name),
    ]),
    `${composeSessionKey}:${forwardAttachmentsLoading}`,
  );

  React.useEffect(() => {
    if (!open) return;
    const acc = sendableAccounts.find((a) => a.id === composeAccountId);
    if (!acc) return;
    setComposeSignatureHtml(acc.signature_html);
    setComposeFromEmail((current) => {
      const choices = mailboxFromChoices(acc.email, acc.email_aliases);
      if (current && choices.includes(current)) return current;
      return resolvedDefaultFrom(acc.email, acc.email_aliases, acc.default_from_email);
    });
  }, [open, sendableAccounts, composeAccountId]);

  const handleSendNewMail = () => {
    const html = (composeEditorRef.current?.innerHTML || composeDraft).trim();
    const text = normalizeMailBody(composeEditorRef.current?.innerText || composeDraft);
    if (forwardAttachmentsLoading) return;
    if (!composeAccountId.trim()) {
      setSendError('Bitte Von-Adresse wählen.');
      return;
    }
    if (!composeTo.trim() || !composeSubject.trim() || !text) {
      setSendError('Bitte Empfänger, Betreff und Nachricht ausfüllen.');
      return;
    }
    const attachError = validateNylasAttachments(
      composeAttachments.map((f) => ({ name: f.name, size: f.size })),
      {
        bodyTextBytes: estimateNylasOutgoingBodyBytes({
          text,
          html,
          signatureHtml: composeSignatureHtml,
        }),
      },
    );
    if (attachError) {
      setAttachmentError(attachError);
      return;
    }
    setComposeSending(true);
    setSendError(null);
    setAttachmentError(null);
    const fd = new FormData();
    fd.append('to', composeTo.trim());
    if (composeCc.trim()) fd.append('cc', composeCc.trim());
    if (forwardSourceEmailId != null) fd.append('forward_source_email_id', String(forwardSourceEmailId));
    fd.append('subject', composeSubject.trim());
    fd.append('text', text);
    if (html) fd.append('html', html);
    if (composeFromEmail.trim()) fd.append('from', composeFromEmail.trim());
    const leadIdValue = leadId != null ? String(leadId).trim() : '';
    if (leadIdValue) fd.append('lead_id', leadIdValue);
    composeAttachments.forEach((file) => fd.append('attachments', file));
    fetch(`/api/inbox/accounts/${composeAccountId}/send`, { method: 'POST', body: fd })
      .then((r) =>
        r.text().then((t) => {
          let d: Record<string, unknown> = {};
          try {
            d = t ? (JSON.parse(t) as Record<string, unknown>) : {};
          } catch {
            d = { raw: t };
          }
          return { ok: r.ok, d };
        }),
      )
      .then(({ ok, d }) => {
        if (d?.deduplicated) {
          throw new Error(String(d?.error ?? 'Diese E-Mail wurde vor Kurzem bereits gesendet.'));
        }
        if (!ok || !d?.success) throw new Error(String(d?.error ?? 'E-Mail konnte nicht gesendet werden.'));
        onClose();
        void onSent?.();
      })
      .catch((err: Error) => setSendError(err.message || 'E-Mail konnte nicht gesendet werden.'))
      .finally(() => setComposeSending(false));
  };

  const handleGenerateAiDraft = React.useCallback(async () => {
    setAiDraftLoading(true);
    setAiDraftError(null);
    try {
      const hasReplyAnchor = Boolean(aiDraftEmailId);
      const endpoint = hasReplyAnchor ? '/api/inbox/reply-draft' : '/api/inbox/compose-draft';
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          hasReplyAnchor
            ? {
                emailId: aiDraftEmailId,
                prompt: aiDraftPrompt.trim(),
              }
            : {
                to: composeTo.trim(),
                cc: composeCc.trim(),
                subject: composeSubject.trim(),
                currentDraft: normalizeMailBody(composeEditorRef.current?.innerText || composeDraft),
                prompt: aiDraftPrompt.trim(),
              }
        ),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.draft) {
        throw new Error(data?.error || 'KI-Entwurf konnte nicht erstellt werden.');
      }
      const draft = String(data.draft || '').trim();
      if (!draft) throw new Error('KI-Entwurf war leer.');
      const suggestedSubject = String(data.subject || '').trim();
      if (suggestedSubject && !composeSubject.trim()) {
        setComposeSubject(suggestedSubject);
      }
      const draftHtml = markdownToEmailHtml(draft);
      if (composeEditorRef.current) {
        composeEditorRef.current.innerHTML = draftHtml;
        composeEditorRef.current.focus();
      }
      setComposeDraft(draftHtml);
      setAiDraftOpen(false);
    } catch (err) {
      setAiDraftError(err instanceof Error ? err.message : 'KI-Entwurf konnte nicht erstellt werden.');
    } finally {
      setAiDraftLoading(false);
    }
  }, [aiDraftEmailId, aiDraftPrompt, composeCc, composeDraft, composeSubject, composeTo]);

  if (!open) return null;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName={overlayClassName}
      panelClassName={showDunningPreview ? 'md:max-w-[1280px]' : 'md:max-w-3xl'}
      disableClose={composeSending}
      ariaLabelledBy="compose-email-title"
      dirty={open && composeDirty}
      discardDescription="Die E-Mail ist noch nicht gesendet. Dein Text geht verloren."
    >
      {(requestClose) => (
        <>
          <div className={`flex h-full min-h-0 w-full overflow-hidden ${showDunningPreview ? 'md:flex-row' : 'flex-col'}`}>
            {showDunningPreview && dunningFile ? (
              <aside className="hidden min-h-0 md:flex md:h-[min(82vh,820px)] md:w-[56%] md:min-w-0 md:flex-col md:border-r md:border-app-border bg-app-surface-muted">
                <div className="shrink-0 truncate border-b border-app-border px-3 py-2 text-xs font-medium text-app-text-muted" title={dunningFile.name}>
                  {dunningFile.name}
                </div>
                <PdfDocumentViewer
                  url={attachmentUrlFor(dunningFile)}
                  title={dunningFile.name}
                  openUrl={attachmentUrlFor(dunningFile)}
                  className="h-full w-full"
                />
              </aside>
            ) : null}
            <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${showDunningPreview ? 'md:h-[min(82vh,820px)]' : 'h-full'}`}>
            <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3 shrink-0">
              <button
                type="button"
                onClick={requestClose}
                className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover"
                aria-label="Zurück"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <p id="compose-email-title" className="text-sm font-semibold text-app-text flex-1">Neue E-Mail</p>
              <button
                type="button"
                disabled={composeSending}
                onClick={requestClose}
                className="hidden md:inline-flex items-center justify-center w-8 h-8 rounded-full border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text text-xs disabled:opacity-50"
              >
                ✕
              </button>
              <button
                type="button"
                onClick={handleSendNewMail}
                disabled={
                  composeSending ||
                  forwardAttachmentsLoading ||
                  !composeAccountId.trim() ||
                  !composeTo.trim() ||
                  !composeSubject.trim() ||
                  !normalizeMailBody(composeEditorRef.current?.innerText || composeDraft).trim()
                }
                className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pipedrive-blue text-white text-sm disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {composeSending ? '…' : 'Senden'}
              </button>
            </div>
            {(sendError || attachmentError) && (
              <div className="mx-4 mt-2 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
                {sendError || attachmentError}
              </div>
            )}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="px-4 pt-3 pb-2 space-y-2 border-b border-app-border shrink-0">
                <div>
                  <label className="block text-xs font-medium text-app-text-muted mb-1">Von</label>
                  {accountsLoading ? (
                    <InlineLoadingState label="Lade Postfächer…" className="justify-start py-2" />
                  ) : fromOptions.length > 1 ? (
                    <AppSelectDropdown
                      value={(() => {
                        const acc = sendableAccounts.find((item) => item.id === composeAccountId);
                        const email = acc
                          ? composeFromEmail || resolvedDefaultFrom(acc.email, acc.email_aliases, acc.default_from_email)
                          : '';
                        return acc && email ? `${acc.id}:${email}` : '';
                      })()}
                      onChange={handleComposeFromChange}
                      options={fromOptions.map((option) => ({ value: option.key, label: option.label }))}
                      placeholder="Bitte Von-Adresse wählen"
                      className="w-full min-w-0"
                      disabled={composeSending}
                    />
                  ) : (
                    <p className="w-full rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text">
                      {fromOptions[0]?.label ?? '—'}
                    </p>
                  )}
                </div>
                <div ref={composeToWrapperRef} className="relative">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label className="block text-xs font-medium text-app-text-muted">An</label>
                    {!showCcField ? (
                      <button
                        type="button"
                        onClick={() => setShowCcField(true)}
                        disabled={composeSending}
                        className="inline-flex items-center rounded-md border border-app-border px-2 py-0.5 text-[11px] font-medium text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                        title="Cc hinzufügen"
                      >
                        Cc
                      </button>
                    ) : null}
                  </div>
                  <input
                    key={`compose-to-${resetKey}`}
                    type="text"
                    value={composeTo}
                    onChange={(e) => {
                      setComposeToInteracted(true);
                      setComposeTo(e.target.value);
                    }}
                    onBlur={() => setTimeout(() => setShowComposeToDropdown(false), 150)}
                    placeholder="empfaenger@beispiel.de, weitere@…"
                    disabled={composeSending}
                    className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                  />
                  {showComposeToDropdown && composeToSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                      {composeToSuggestions.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const parts = composeTo.split(',');
                            parts[parts.length - 1] = ' ' + s.email;
                            setComposeTo(parts.join(',').trimStart());
                            setShowComposeToDropdown(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-app-hover"
                        >
                          <span className="text-sm font-medium text-app-text truncate">{s.label}</span>
                          <span className="text-xs text-app-text-subtle truncate ml-auto shrink-0">{s.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {showCcField ? (
                  <div>
                    <label className="block text-xs font-medium text-app-text-muted mb-1">Cc</label>
                    <input
                      type="text"
                      value={composeCc}
                      onChange={(e) => setComposeCc(e.target.value)}
                      placeholder="cc@beispiel.de, weitere@…"
                      disabled={composeSending}
                      className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                    />
                  </div>
                ) : null}
                <div>
                  <label className="block text-xs font-medium text-app-text-muted mb-1">Betreff</label>
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Betreff"
                    disabled={composeSending}
                    className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                  />
                </div>
              </div>
              <div className="px-4 py-2 border-b border-app-border shrink-0 overflow-x-auto">
                <div className="flex items-center gap-1 text-app-text-subtle shrink-0">
                  <ReplyToolbarButton
                    icon={Bold}
                    title="Fett"
                    onClick={() => {
                      composeEditorRef.current?.focus();
                      document.execCommand('bold');
                    }}
                  />
                  <ReplyToolbarButton
                    icon={Italic}
                    title="Kursiv"
                    onClick={() => {
                      composeEditorRef.current?.focus();
                      document.execCommand('italic');
                    }}
                  />
                  <ReplyToolbarButton
                    icon={Underline}
                    title="Unterstrichen"
                    onClick={() => {
                      composeEditorRef.current?.focus();
                      document.execCommand('underline');
                    }}
                  />
                  <span className="w-px h-4 bg-app-border mx-1" />
                  <ReplyToolbarButton
                    icon={LinkIcon}
                    title="Link einfügen"
                    onClick={() => {
                      const url = prompt('Link-URL eingeben:');
                      if (!url) return;
                      composeEditorRef.current?.focus();
                      document.execCommand('createLink', false, url);
                    }}
                  />
                  <span className="w-px h-4 bg-app-border mx-1" />
                  <ReplyToolbarButton
                    icon={List}
                    title="Aufzählung"
                    onClick={() => {
                      composeEditorRef.current?.focus();
                      document.execCommand('insertUnorderedList');
                    }}
                  />
                  <ReplyToolbarButton
                    icon={ListOrdered}
                    title="Nummerierte Liste"
                    onClick={() => {
                      composeEditorRef.current?.focus();
                      document.execCommand('insertOrderedList');
                    }}
                  />
                  <span className="w-px h-4 bg-app-border mx-1" />
                  <ReplyToolbarButton
                    icon={Paperclip}
                    title="Datei anhängen"
                    onClick={() => composeFileInputRef.current?.click()}
                  />
                  <input
                    ref={composeFileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (picked.length === 0) return;
                      setComposeAttachments((prev) => {
                        const { files, error } = mergeNylasAttachmentFiles(prev, picked);
                        setAttachmentError(error);
                        return files;
                      });
                    }}
                  />
                  {composeAttachments.length > 0 ? (
                    <button
                      ref={attachmentTriggerRef}
                      type="button"
                      onClick={() => {
                        if (attachmentMenuOpen) setAttachmentMenuOpen(false);
                        else openAttachmentMenu();
                      }}
                      disabled={composeSending}
                      aria-haspopup="listbox"
                      aria-expanded={attachmentMenuOpen}
                      title="Anhänge ansehen oder entfernen"
                      className="inline-flex items-center gap-0.5 text-[11px] text-app-text-subtle ml-1 shrink-0 hover:text-app-text hover:underline disabled:opacity-50"
                    >
                      {composeAttachments.length === 1
                        ? '1 Datei angehängt'
                        : `${composeAttachments.length} Dateien angehängt`}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  ) : null}
                  {forwardAttachmentsLoading ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-app-text-subtle ml-1 shrink-0">
                      <LoadingSpinner size="sm" />
                      Anhänge werden übernommen…
                    </span>
                  ) : null}
                  {aiDraftAvailable && (
                  <div className="ml-auto pl-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAiDraftError(null);
                        setAiDraftOpen((open) => {
                          const next = !open;
                          if (next) setTimeout(() => aiDraftInputRef.current?.focus(), 30);
                          return next;
                        });
                      }}
                      disabled={composeSending || aiDraftLoading}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs md:text-sm disabled:opacity-50 ${
                        aiDraftOpen
                          ? 'border border-violet-200 bg-violet-50 text-violet-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title="KI-Antwortentwurf mit Claude"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      KI-Antwort
                    </button>
                  </div>
                  )}
                </div>
              </div>
              {aiDraftAvailable && aiDraftOpen && (
                <div className="shrink-0 bg-violet-50 border-b border-violet-100">
                  <div className="flex items-start gap-2 px-4 py-2.5">
                    <Sparkles className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-2" />
                    <textarea
                      ref={aiDraftInputRef}
                      value={aiDraftPrompt}
                      onChange={(e) => setAiDraftPrompt(e.target.value)}
                      placeholder="Optional: Stil oder Inhalt beschreiben …"
                      disabled={aiDraftLoading}
                      rows={1}
                      className="flex-1 min-h-0 resize-none overflow-hidden border-0 bg-transparent px-0 py-1 text-sm leading-5 text-app-text placeholder:text-violet-400/70 focus:outline-none focus:ring-0 disabled:opacity-60"
                    />
                    <div className="flex shrink-0 items-center gap-1 mt-1">
                      <button
                        type="button"
                        onClick={() => setAiDraftOpen(false)}
                        disabled={aiDraftLoading}
                        className="px-2 py-1 text-xs text-violet-600 hover:text-violet-800 disabled:opacity-50"
                      >
                        Schließen
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleGenerateAiDraft()}
                        disabled={aiDraftLoading}
                        className="px-2.5 py-1 text-xs font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
                      >
                        {aiDraftLoading ? '…' : 'Entwerfen'}
                      </button>
                    </div>
                  </div>
                  {aiDraftError && (
                    <p className="px-4 pb-2 text-xs text-red-600">{aiDraftError}</p>
                  )}
                </div>
              )}
              <div
                ref={composeEditorRef}
                contentEditable={!composeSending}
                suppressContentEditableWarning
                onInput={(ev) => {
                  const el = ev.currentTarget;
                  setComposeDraft(el.innerText || '');
                }}
                onKeyDown={(e) => {
                  if (handleContentEditableListKeyDown(e, composeEditorRef.current)) {
                    setComposeDraft(composeEditorRef.current?.innerText || '');
                  }
                }}
                data-placeholder="Nachricht verfassen …"
                className={`flex-1 overflow-y-auto px-4 py-3 text-sm outline-none whitespace-pre-wrap [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:list-outside empty:before:content-[attr(data-placeholder)] empty:before:text-app-text-subtle empty:before:pointer-events-none ${
                  showDunningPreview ? 'min-h-0' : 'min-h-[240px] md:min-h-[320px]'
                }`}
              />
            </div>
            {dunningPdfError ? (
              <p className="px-4 pt-2 text-xs text-red-600 shrink-0">{dunningPdfError}</p>
            ) : null}
            <div className="flex items-center justify-start gap-2 shrink-0 px-4 py-3 border-t border-app-border bg-app-surface">
                {dunningDocument && !hasDunningPdf ? (
                  <button
                    type="button"
                    onClick={() => void handleCreateDunningDocument()}
                    disabled={composeSending || dunningPdfBusy}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md px-1.5 -ml-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50"
                  >
                    {dunningPdfBusy ? <LoadingSpinner size="sm" /> : <FileText className="h-4 w-4" />}
                    {dunningPdfBusy ? 'Wird erstellt…' : 'Mahnungsdokument erstellen'}
                  </button>
                ) : dunningDocument ? (
                  <button
                    type="button"
                    onClick={() => void handleCreateDunningDocument()}
                    disabled={composeSending || dunningPdfBusy}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md px-1.5 -ml-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50 md:hidden"
                  >
                    <FileText className="h-4 w-4" />
                    Mahnungsdokument ansehen
                  </button>
                ) : (
                  <span />
                )}
                <div className="hidden md:flex justify-end gap-2 ml-auto">
                <button
                  type="button"
                  disabled={composeSending}
                  onClick={() => onClose()}
                  className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleSendNewMail}
                  disabled={
                    composeSending ||
                    forwardAttachmentsLoading ||
                    !composeAccountId.trim() ||
                    !composeTo.trim() ||
                    !composeSubject.trim() ||
                    !normalizeMailBody(composeEditorRef.current?.innerText || composeDraft).trim()
                  }
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  {composeSending ? 'Wird gesendet…' : 'Senden'}
                </button>
                </div>
            </div>
            </div>
          </div>
          {attachmentMenuOpen && attachmentMenuPos
            ? createPortal(
                <div
                  ref={attachmentMenuRef}
                  role="listbox"
                  aria-label="Anhänge"
                  style={{
                    position: 'fixed',
                    top: attachmentMenuPos.top,
                    left: attachmentMenuPos.left,
                    width: attachmentMenuPos.width,
                    zIndex: UI_Z_INDEX.portalDropdownInModal,
                  }}
                  className="overflow-hidden rounded-md border border-app-border bg-app-surface shadow-lg"
                >
                  {composeAttachments.map((file, index) => (
                    <div
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex w-full items-center hover:bg-app-hover"
                    >
                      <button
                        type="button"
                        role="option"
                        onClick={() => openAttachmentPreview(file)}
                        className="flex min-w-0 flex-1 items-center px-3 py-2 text-left text-sm text-app-text"
                        title={file.name}
                      >
                        <span className="truncate">{file.name}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAttachmentError(null);
                          setComposeAttachments((prev) => {
                            const next = prev.filter((item) => item !== file);
                            if (next.length === 0) setAttachmentMenuOpen(false);
                            return next;
                          });
                        }}
                        disabled={composeSending}
                        className="mr-1 shrink-0 rounded p-1.5 text-app-text-subtle hover:bg-app-surface-muted hover:text-app-text disabled:opacity-50"
                        title={`${file.name} entfernen`}
                        aria-label={`${file.name} entfernen`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>,
                document.body,
              )
            : null}
          <InboxAttachmentPreviewModal
            attachment={previewAttachment}
            onClose={() => setPreviewAttachment(null)}
            allowMailShare={false}
          />
        </>
      )}
    </AppModal>
  );
}
